import React from "react";
import Footer from "./Footer";
import Navbar from "./Navbar";
import { FaCheckCircle, FaShieldAlt } from "react-icons/fa";
import WhyChooseUs from "./WhyChooseUs";
import CheckEligibility from "./CheckEligibility";

// Premium Assets
import collaboration_professional from "../assets/premium/collaboration_professional.png";

const AboutUs: React.FC = () => {
  return (
    <div className="min-h-screen w-full bg-white text-[var(--text-primary)] relative overflow-x-hidden font-sans selection:bg-[var(--primary)] selection:text-white">
      <div className="relative z-10 flex w-full flex-col items-center">
        <Navbar />

        {/* About Hero Section */}
        <section className="w-full bg-[var(--secondary)] pt-32 pb-16 px-6 lg:px-20 flex items-center relative overflow-hidden border-b border-[var(--border-light)]">
          {/* Subtle Background Accents */}
          <div className="absolute top-[-10%] left-[-5%] w-[30vw] h-[30vw] bg-[var(--primary)] opacity-[0.02] rounded-full blur-[100px] pointer-events-none" />

          <div className="max-w-5xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center relative z-10 py-6 sm:py-12">
            
            {/* Left Content Column (7 cols) */}
            <div className="lg:col-span-7 flex flex-col items-start gap-5 text-left w-full">
              <h1 className="text-4xl sm:text-5xl lg:text-[54px] font-black text-[#0F172A] leading-tight tracking-tight">
                About <span className="text-[var(--primary)]">SwiftPaisa</span>
              </h1>

              <div className="space-y-4 max-w-xl">
                <p className="text-lg sm:text-[20px] font-black text-[#1E293B] leading-snug">
                  Empowering professionals with instant salary access when it matters most. 💸
                </p>
                <p className="text-slate-500 font-medium text-[14px] sm:text-[15px] leading-relaxed">
                  We're on a mission to bridge the gap between paydays. Life doesn't wait for your salary cycle, and now, you don't have to either. We've simplified the entire experience to be fair, fast, and fully transparent.
                </p>
              </div>
            </div>

            {/* Right Image Column (5 cols) */}
            <div className="lg:col-span-5 relative flex justify-center items-center w-full">
              <div className="relative w-full max-w-[420px] aspect-[4/3] sm:aspect-square rounded-[24px] overflow-hidden shadow-xl border border-slate-100/50 group cursor-pointer">
                <img
                  src={collaboration_professional}
                  alt="SwiftPaisa Professional Team"
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--primary)]/5 to-transparent pointer-events-none" />
              </div>

              {/* Floating Stats Card */}
              <div className="absolute -bottom-4 -left-4 bg-white p-4 rounded-xl shadow-md border border-slate-100 hidden sm:block animate-bounce-slow">
                <div className="text-[var(--primary)] text-xl font-black mb-0.5">100%</div>
                <div className="text-slate-400 font-black text-[9px] uppercase tracking-widest">Secure & Fair</div>
              </div>
            </div>

          </div>
        </section>

        {/* Mission & Vision Section - Premium Top-Accented Cards */}
        <section className="w-full py-16 sm:py-20 px-6 lg:px-20 bg-white relative z-10">
          <div className="max-w-5xl mx-auto w-full grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                title: "Our Mission",
                desc: "To provide every working professional with immediate, fair, and responsible access to their earned income. We're leveraging technology to end the stress of the payday gap.",
                icon: <FaShieldAlt className="w-5 h-5" />,
                watermark: "M"
              },
              {
                title: "Our Vision",
                desc: "To become the most trusted financial companion for modern employees, building a future where your hard-earned money is always just a tap away.",
                icon: <FaCheckCircle className="w-5 h-5" />,
                watermark: "V"
              }
            ].map((item, idx) => (
              <div
                key={idx}
                className="group relative p-8 rounded-[24px] border border-slate-100/80 shadow-[0_10px_25px_rgba(0,0,0,0.02)] transition-all duration-500 overflow-hidden bg-white text-left flex flex-col justify-between gap-6"
              >
                {/* Top accent line */}
                <div className="absolute top-0 left-0 right-0 h-[4px] bg-[var(--primary)]" />
                
                {/* Background Letter Watermark */}
                <div className="absolute right-[5px] bottom-[-25px] text-slate-100/[0.12] text-[150px] font-black pointer-events-none select-none rotate-[8deg] z-0 leading-none group-hover:text-slate-100/[0.22] transition-colors">
                  {item.watermark}
                </div>

                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-xl bg-white/[0.04] border border-slate-100 flex items-center justify-center mb-6 text-[var(--primary)] shadow-sm">
                    {item.icon}
                  </div>

                  <h2 className="text-xl sm:text-2xl font-black text-[#0F172A] mb-3 tracking-tight">
                    {item.title}
                  </h2>
                  <p className="text-slate-500 text-[14px] sm:text-[15px] font-medium leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Reusing Premium Sections */}
        <div className="w-full bg-white relative z-10 flex flex-col items-center">
          <WhyChooseUs />
          <CheckEligibility />
        </div>

        <Footer />
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 4s ease-in-out infinite;
        }
      `}} />
    </div>
  );
};

export default AboutUs;
